import type { ReactNode } from 'react';
import { InfoTip } from './InfoTip';

/**
 * PageHeader (design system v1): the single header of a page.
 *
 *   Breadcrumb: Module / Page                      (text-sm, muted)
 *   Title (the page's only h1)        [secondary] [primary]
 *   Summary: one italic sentence, directly below the h1, never beside it
 *   [tabs]
 *
 * Nothing goes between the title and the summary. How the page works belongs
 * in `info` (an InfoTip after the title), not in the summary.
 *
 * Text sits directly on the app page, so it uses the page aliases
 * (text-body / text-muted), which follow the page when the shell changes theme.
 */
export function PageHeader({
  title,
  summary,
  breadcrumb,
  info,
  actions,
  children,
}: {
  title: ReactNode;
  /** One sentence. Rendered italic, directly under the h1. */
  summary?: ReactNode;
  breadcrumb?: ReactNode[];
  /** Short explanation shown in an InfoTip after the title. */
  info?: string;
  /** Right-aligned buttons: at most one primary, the rest secondary. Wraps under the title on mobile. */
  actions?: ReactNode;
  /** Tabs or a segmented control directly beneath. */
  children?: ReactNode;
}) {
  return (
    <header className="space-y-3">
      {breadcrumb && breadcrumb.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-muted">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <span aria-hidden="true" className="text-faint">/</span>}
              {b}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 font-sans text-2xl leading-8 font-semibold tracking-tighter text-body">
            {title}
            {info && <InfoTip text={info} label={typeof title === 'string' ? title : undefined} side="bottom" align="start" tone="page" />}
          </h1>
          {summary && <p className="mt-0.5 text-sm italic text-muted">{summary}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}
